import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/**
 * useTelegram Hook
 * 
 * Updated to support the platform-owned bot QR deep-link flow.
 */
export function useTelegram() {
  const { getAccessToken } = useAuth();

  /**
   * Create a connection session (get QR URL)
   */
  const createConnectSession = useCallback(async (deploymentId) => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/integrations/telegram/connect-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ deploymentId }),
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to create connection session');
    }
    return data; // { qrUrl, expiresAt, status }
  }, [getAccessToken]);

  /**
   * Check the status of a connection for a specific deployment
   */
  const getConnectionStatus = useCallback(async (deploymentId) => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/integrations/telegram/status?deploymentId=${deploymentId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch connection status');
    }
    return data; // { status, chatIdMasked, connectedAt }
  }, [getAccessToken]);

  /**
   * Disconnect Telegram from a deployment
   */
  const disconnectTelegram = useCallback(async (deploymentId) => {
    const token = getAccessToken();
    // Re-using the DELETE logic if needed, or implement a specific disconnect
    const response = await fetch(`${API_BASE}/integrations/telegram/disconnect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ deploymentId }),
    });
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to disconnect');
    }
    return true;
  }, [getAccessToken]);

  return {
    createConnectSession,
    getConnectionStatus,
    disconnectTelegram
  };
}
